import { useNavigate } from "react-router-dom";
import { toAbsoluteUrl } from "../../../_metronic/helpers";
import { loginLogout } from "../projectData";
import { useEffect, useState } from "react";
import { getUserDashboard } from "../api";
import { useRouteData } from "../RouteDataContext";

/**
 * UserProfileSection Component
 * User profile display at the top of dashboard
 */
const UserProfileSection = ({ userData }) => {
    return (
        <div className="card shadow-sm border-0 overflow-hidden mt-4 mb-4"
            style={{
                borderRadius: "20px",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
            }}>
            <div className="card-body p-4">
                <div className="d-flex align-items-center">
                    {/* Profile Avatar */}
                    <div className="position-relative me-4">
                        <div className="symbol symbol-70px symbol-circle"
                            style={{
                                border: "3px solid #e2e8f0",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                            }}>
                            <img
                                src={userData?.user?.profile_image || toAbsoluteUrl('/media/avatars/300-1.jpg')}
                                alt={userData?.user?.first_name}
                                className="symbol-label"
                                style={{ objectFit: 'cover' }}
                            />
                        </div>
                        {/* Online Status */}
                        <div className="position-absolute bottom-0 end-0 bg-success rounded-circle"
                            style={{
                                width: "18px",
                                height: "18px",
                                border: "3px solid white",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}>
                        </div>
                    </div>

                    {/* User Information */}
                    <div className="flex-grow-1">
                        <div className="d-flex flex-column">
                            {/* Greeting */}
                            <div className="text-muted fs-7 fw-semibold mb-1">
                                Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}!
                            </div>

                            {/* Name */}
                            <div className="fw-bolder fs-4 text-dark mb-2">
                                {userData?.user?.first_name} {userData?.user?.last_name}
                            </div>

                            {/* Contact Info Row */}
                            <div className="d-flex flex-wrap gap-3">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2"
                                        style={{ width: "28px", height: "28px" }}>
                                        <i className="bi bi-phone-fill text-primary fs-8"></i>
                                    </div>
                                    <span className="fw-semibold fs-7 text-gray-700">
                                        {userData?.user?.phone}
                                    </span>
                                </div>

                                {userData?.user?.email && (
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-2 me-2"
                                            style={{ width: "28px", height: "28px" }}>
                                            <i className="bi bi-envelope-fill text-success fs-8"></i>
                                        </div>
                                        <span className="fw-semibold fs-7 text-gray-700">
                                            {userData?.user?.email}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Address Info (if available) */}
                {userData?.user?.address?.address && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
                        <div className="d-flex align-items-center">
                            <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3"
                                style={{ width: "28px", height: "28px" }}>
                                <i className="bi bi-geo-alt-fill text-warning fs-8"></i>
                            </div>
                            <span className="fw-semibold fs-7 text-gray-600">
                                {userData.user.address.address}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * ProjectBanner Component
 * Professional banner display for the project with dynamic image selection
 */
const ProjectBanner = ({ projectData, handlePlayGame }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Banner image selection
    const getBannerImage = () => {
        if (projectData?.banner) {
            return toAbsoluteUrl(projectData.banner);
        }
        const projectName = projectData?.pname?.toLowerCase();
        switch (projectName) {
            case 'fmc_aksya_patrara':
                return toAbsoluteUrl('/media/images/akp6_banner.jpg');
            case 'mitr':
                return toAbsoluteUrl('/media/images/mitr-banner3.png');
            case 'corteva-galileo':
                return toAbsoluteUrl('/media/images/corteva-galileo-cumin.png');
            case 'greenply':
                return toAbsoluteUrl('/media/images/green-ply-banner.png');
            default:
                return toAbsoluteUrl('/media/images/akp6_banner.jpg');
        }
    };

    return (
        <div className="mb-6">
            <div
                className="card border-0 overflow-hidden"
                style={{ borderRadius: "20px" }}
            >
                {/* Banner image only */}
                <div className="position-relative" style={{ height: "220px" }}>
                    {!imageLoaded && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                            style={{ backgroundColor: "#f8f9fa" }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading banner...</span>
                            </div>
                        </div>
                    )}
                    <img
                        src={getBannerImage()}
                        alt={`${projectData?.pname || 'Akshaya Patra'} Banner`}
                        className="w-100 h-100"
                        style={{
                            objectFit: "cover",
                            filter: imageError ? "brightness(0.7) blur(1px)" : "brightness(0.85) contrast(1.1)",
                            transition: "all 0.3s ease",
                            opacity: imageLoaded ? 1 : 0
                        }}
                        onLoad={() => setImageLoaded(true)}
                        onError={(e) => {
                            setImageError(true);
                            setImageLoaded(true);
                            if (e.target.src !== toAbsoluteUrl('/media/images/akp6_banner.jpg')) {
                                e.target.src = toAbsoluteUrl('/media/images/akp6_banner.jpg');
                            }
                        }}
                    />
                </div>

                {/* Play Game Button section */}
                <div className="card-body" style={{ background: "linear-gradient(90deg, #17233d 0%, #274690 100%)", borderBottomLeftRadius: "20px", borderBottomRightRadius: "20px" }}>
                    <div className="text-center">
                        <button
                            className="btn w-100 d-flex align-items-center justify-content-center p-3 shadow-lg position-relative"
                            style={{
                                borderRadius: "16px",
                                background: "linear-gradient(100deg, #33bfff 0%, #4f8cff 80%)",
                                border: "none",
                                boxShadow: "0 6px 24px 0 rgba(50, 112, 245, 0.15)",
                                color: "#fff",
                                overflow: "hidden",
                                position: "relative",
                                transition: "all 0.24s cubic-bezier(.4,2,.6,1)"
                            }}
                            onClick={handlePlayGame}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = "linear-gradient(100deg, #ffd700 0%, #ff9900 100%)";
                                e.currentTarget.style.color = "#282828";
                                e.currentTarget.style.transform = "scale(1.03)";
                                e.currentTarget.querySelector('.game-glow').style.opacity = 1;
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = "linear-gradient(100deg, #33bfff 0%, #4f8cff 80%)";
                                e.currentTarget.style.color = "#fff";
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.querySelector('.game-glow').style.opacity = 0;
                            }}
                        >
                            <span
                                className="game-glow"
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: "100%",
                                    height: "100%",
                                    background: "radial-gradient(circle at 20% 20%, #fff9e6 0%, #fff2a8 60%, transparent 100%)",
                                    opacity: 0,
                                    pointerEvents: "none",
                                    zIndex: 0,
                                    borderRadius: "inherit",
                                    transition: "opacity 0.26s"
                                }}
                            ></span>
                            <div className="d-flex align-items-center position-relative" style={{ zIndex: 1 }}>
                                <div className="glow-icon d-flex align-items-center justify-content-center" style={{
                                    background: "linear-gradient(135deg, #ffffff22, #4f8cff99)",
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 16px 0 #4f8cff66",
                                    padding: "13px",
                                    marginRight: "18px",
                                    transition: "background 0.23s"
                                }}>
                                    <i className="bi bi-controller" style={{ color: "#fff", fontSize: "1.6rem", textShadow: "0 2px 14px #33bfff88" }}></i>
                                </div>
                                <div className="text-start">
                                    <div className="fw-bolder fs-5" style={{
                                        letterSpacing: "0.03em",
                                        textShadow: "0 2px 6px #25337d46",
                                        color: "inherit"
                                    }}>Play Game</div>
                                    <div className="fs-8 mt-1" style={{
                                        color: "#fffbe6",
                                        fontWeight: 500,
                                        letterSpacing: "0.01em"
                                    }}>
                                        <i className="bi bi-gift-fill me-1" style={{ color: "#FFD700", fontSize: "1rem" }}></i>
                                        Win Exciting Rewards!
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * ActionButtonsRow Component
 * Two main action buttons like in mobile app
 */
const ActionButtonsRow = ({ handleSubmitPayment, handlePaymentHistory }) => {
    return (
        <div className="row g-3 mb-6">
            {/* Submit Your Payment Button */}
            <div className="col-6">
                <button
                    className="btn w-100 d-flex flex-row align-items-center justify-content-between p-6 text-white"
                    style={{
                        background: "#1b986f",
                        borderRadius: "12px",
                        border: "none",
                        minHeight: "80px",
                        //boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)"
                    }}
                    onClick={handleSubmitPayment}
                >
                    <span className="fw-bold text-start">
                        Submit Your <br /> Payment
                    </span>
                    <i className="bi fw-bold text-white bi-chevron-right fs-4"></i>
                </button>
            </div>

            {/* View Payment Summary Button */}
            <div className="col-6">
                <button
                    className="btn w-100 d-flex flex-row align-items-center justify-content-between p-6 text-white"
                    style={{
                        background: "#192766",
                        borderRadius: "12px",
                        border: "none",
                        minHeight: "80px",
                        //boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)"
                    }}
                    onClick={handleSubmitPayment}
                >
                    <span className="fw-bold text-start">
                        View Payment <br /> Summary
                    </span>
                    <i className="bi fw-bold text-white bi-chevron-right fs-4"></i>
                </button>
            </div>

        </div>
    );
};

// /**
//  * ActionCard Component
//  * Professional action card with enhanced design and animations
//  */
// const ActionCard = ({ title, description, icon, bgColor, onClick, disabled = false, badge }) => {
//     return (
//         <div
//             className={`card shadow-lg h-100 ${disabled ? '' : 'cursor-pointer'}`}
//             style={{
//                 border: "none",
//                 borderRadius: "16px",
//                 transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
//                 opacity: disabled ? 0.7 : 1,
//                 transform: "translateY(0)",
//                 background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
//             }}
//             onClick={disabled ? undefined : onClick}
//             onMouseEnter={(e) => {
//                 if (!disabled) {
//                     e.currentTarget.style.transform = "translateY(-8px)";
//                     e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.1)";
//                 }
//             }}
//             onMouseLeave={(e) => {
//                 if (!disabled) {
//                     e.currentTarget.style.transform = "translateY(0)";
//                     e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)";
//                 }
//             }}
//         >
//             <div className="card-body p-6 d-flex flex-column h-100">
//                 {/* Header Section */}
//                 <div className="d-flex align-items-start mb-4">
//                     <div className="position-relative me-4">
//                         <div
//                             className="symbol symbol-60px symbol-circle d-flex align-items-center justify-content-center"
//                             style={{
//                                 background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
//                                 boxShadow: `0 8px 24px ${bgColor}40`
//                             }}
//                         >
//                             <i className={`${icon} text-white fs-1`}></i>
//                         </div>

//                         {/* Pulse Animation for Active Cards */}
//                         {!disabled && (
//                             <div className="position-absolute top-0 start-0 w-100 h-100 rounded-circle"
//                                 style={{
//                                     background: `${bgColor}30`,
//                                     animation: "pulse 2s infinite"
//                                 }}>
//                             </div>
//                         )}
//                     </div>

//                     <div className="flex-grow-1">
//                         <div className="d-flex align-items-center justify-content-between mb-2">
//                             <h4 className="fw-bolder text-dark mb-0" style={{ fontSize: "1.25rem" }}>
//                                 {title}
//                             </h4>
//                             {badge && (
//                                 <span className="badge badge-light-success fs-8 fw-semibold px-2 py-1">
//                                     {badge}
//                                 </span>
//                             )}
//                         </div>
//                         <p className="text-muted fs-6 mb-0 lh-lg" style={{ lineHeight: "1.6" }}>
//                             {description}
//                         </p>
//                     </div>
//                 </div>

//                 {/* Spacer */}
//                 <div className="flex-grow-1"></div>

//                 {/* Footer Section */}
//                 <div className="d-flex justify-content-between align-items-center pt-3"
//                     style={{ borderTop: "1px solid #f1f5f9" }}>
//                     <div className="d-flex align-items-center">
//                         <span className={`badge ${disabled
//                             ? 'bg-light text-muted'
//                             : 'bg-light-primary text-primary'
//                             } fs-8 fw-semibold px-3 py-2 rounded-pill`}>
//                             {disabled ? (
//                                 <>
//                                     <i className="bi bi-clock me-1"></i>
//                                     Coming Soon
//                                 </>
//                             ) : (
//                                 <>
//                                     <i className="bi bi-arrow-right-circle me-1"></i>
//                                     Access Now
//                                 </>
//                             )}
//                         </span>
//                     </div>

//                     <div className={`rounded-circle p-2 ${disabled
//                         ? 'bg-light'
//                         : 'bg-light-primary'
//                         }`} style={{ width: "36px", height: "36px" }}>
//                         <i className={`bi bi-arrow-right ${disabled ? 'text-muted' : 'text-primary'
//                             } fs-6`}></i>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

/**
 * EligibilitySection Component
 * Mobile-style eligibility section with blue card
 */
const EligibilitySection = ({ dashboard }) => {
    return (
        <div className="mb-6">
            <h3 className="fw-bold text-dark mb-3">
                Eligibility
            </h3>

            <div className="card border-0 shadow-sm"
                style={{
                    background: "#1a2942",
                    borderRadius: "15px"
                }}>
                <div className="card-body p-4">
                    <div className="d-flex align-items-start text-white">
                        {/* Icon */}
                        <div className="me-3 flex-shrink-0">
                            <div className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: "50px", height: "50px" }}>
                                <i className="bi bi-currency-rupee text-dark fs-4 fw-bold"></i>
                            </div>
                        </div>

                        {/* Text Content */}
                        <div className="flex-grow-1">
                            <h4 className="text-white"> Royal Club Retailers must do a minimum</h4>
                            <div className="fw-semibold fs-6 lh-base" style={{ fontSize: "0.95rem" }}>
                                Transaction of Rs 50,000 and then multiple of 50,000 to their respective distributors.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * BottomGridSection Component
 * Bottom grid with Scheme Detail and Help Support
 */
const BottomGridSection = ({ handlePlayGame }) => {
    return (
        <div className="row g-3 mb-6">
            {/* Scheme Detail */}
            <div className="col-6">
                <div className="card  shadow-sm h-100 position-relative"
                    style={{
                        borderRadius: "15px",
                        background: "#ffffff",
                        border: "1px solid #1b986f2e"
                    }}>
                    <div className="card-body d-flex flex-column align-items-center text-center p-4"
                        style={{ minHeight: "200px" }}>
                        {/* Icon */}
                        <div className="mb-3 mt-2">
                            <div className="rounded d-flex align-items-center justify-content-center"
                                style={{
                                    width: "50px",
                                    height: "60px",
                                    background: "#eaf5f1",
                                    fontSize: "29px",
                                    borderRadius: "2px",
                                }}>
                                <i className="bi bi-file-earmark-text" style={{ color: "#28a745", fontSize: "22px" }}></i>
                            </div>
                        </div>

                        {/* Text */}
                        <div className="mb-3">
                            <div className="fw-bold text-gray-500 text-dark fs-5" >Scheme</div>
                            <div className="fw-bold text-dark fs-3">Detail</div>
                        </div>

                        {/* Arrow Button - positioned at bottom right */}
                        <div className="position-absolute" style={{ bottom: "20px", right: "20px" }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    background: "#1b986f"
                                }}>
                                <i className="bi bi-chevron-right text-white fw-bold fs-5"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Help Support */}
            <div className="col-6">
                <div className="card  shadow-sm h-100 position-relative"
                    style={{
                        borderRadius: "15px",
                        background: "#ffffff",
                        border: "1px solid #1b986f2e"
                    }}>
                    <div className="card-body d-flex flex-column align-items-center text-center p-4"
                        style={{ minHeight: "200px" }}>
                        {/* Icon */}
                        <div className="mb-3 mt-2">
                            <div className="rounded d-flex align-items-center justify-content-center"
                                style={{
                                    width: "50px",
                                    height: "60px",
                                    background: "#f9fded",
                                    fontSize: "29px",
                                    borderRadius: "2px",
                                }}>
                                <i className="bi bi-question-circle" style={{ color: "#c8d62d", fontSize: "22px" }}></i>
                            </div>
                        </div>

                        {/* Text */}
                        <div className="mb-3">
                            <div className="fw-bold text-gray-500 text-dark fs-5" >Help</div>
                            <div className="fw-bold text-dark fs-3">Support</div>
                        </div>

                        {/* Arrow Button - positioned at bottom right */}
                        <div className="position-absolute" style={{ bottom: "20px", right: "20px" }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    background: "#1b986f"
                                }}>
                                <i className="bi bi-chevron-right text-white fw-bold fs-5"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * FmcAksyaPatraraDashboard component displays the main dashboard for FMC Aksya Patrara users
 */
const FmcAksyaPatraraDashboard = ({ projectData }) => {
    const navigate = useNavigate();
    const { userData } = useRouteData();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userData && projectData) {
            getUserDashboard({ id: userData?.user?.iirpl_sap_code, project_key: projectData?.pkey })
                .then((d) => {
                    setDashboard(d?.data);
                    setLoading(false);
                })
                .catch((error) => {
                    loginLogout(error.response);
                    setLoading(false);
                });
        }
    }, [userData, projectData]);

    // Handle navigation actions
    const handleSubmitPayment = () => {
        navigate('/customer/submit-payment/distributor');
    };

    const handlePaymentHistory = () => {
        navigate('/customer/payment-history');
    };

    const handlePlayGame = () => {
        // Navigate to game or show game modal
        navigate('/customer/play-game');
    };

    const handleRewards = () => {
        // Navigate to rewards section
        navigate('/customer/rewards');
    };

    if (loading) {
        return (
            <div className="container py-0 px-0">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Mobile Header */}
            {/* <MobileHeader /> */}

            <div className="container px-4"
                style={{
                    //  background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)",
                    minHeight: "100vh"
                }}>

                {/* User Profile Section */}
                {userData && <UserProfileSection userData={userData} />}

                {/* Project Banner Section */}
                <ProjectBanner projectData={projectData} handlePlayGame={handlePlayGame} handleRewards={handleRewards} />

                {/* Action Buttons Row */}
                <ActionButtonsRow
                    handleSubmitPayment={handleSubmitPayment}
                    handlePaymentHistory={handlePaymentHistory}
                />

                {/* Eligibility Section */}
                <EligibilitySection dashboard={dashboard} />

                {/* Bottom Grid Section */}
                <BottomGridSection handlePlayGame={handlePlayGame} />

                {/* Footer Space */}
                <div style={{ height: "60px" }}></div>
            </div>
        </>
    );
};

export default FmcAksyaPatraraDashboard;